const HEART_SELECTOR = '[data-favorite-button]';
const ADD_TO_CART_SELECTOR = '[data-favorites-add-to-cart]';
const PAGE_SELECTOR = '[data-favorites-page]';
const COUNT_SELECTOR = '[data-favorites-count]';
const PAGE_COUNT_SELECTOR = '[data-favorites-page-count]';
const FAVORITE_STAT_SELECTOR = '[data-favorite-stat]';

const config = window.StyleNinjaFavorites || {};

function proxyUrl(params = {}) {
  const url = new URL(config.proxyUrl || '/apps/style-ninja-favorites', window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
  });
  url.searchParams.set('_', Date.now().toString());
  return url.toString();
}

function loginUrl() {
  const url = new URL(config.loginUrl || '/account', window.location.origin);
  url.searchParams.set('return_url', window.location.pathname + window.location.search);
  return url.toString();
}

async function requestFavorites(options = {}) {
  const response = await fetch(proxyUrl(options.query), {
    method: options.method || 'GET',
    headers: { 'Content-Type': 'application/json' },
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: 'same-origin',
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    const error = new Error(payload.error || `favorites_${response.status}`);
    error.payload = payload;
    throw error;
  }
  return payload;
}

function setButtonState(button, active) {
  button.classList.toggle('is-favorited', active);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
  const label = button.querySelector('[data-favorite-label]');
  if (label) label.textContent = active ? 'Saved to Favorites' : 'Save to Favorites';
}

function setCount(count) {
  document.querySelectorAll(COUNT_SELECTOR).forEach((element) => {
    element.textContent = count > 0 ? String(count) : '';
    element.toggleAttribute('hidden', count <= 0);
  });
  setPageCount(count);
}

function setPageCount(count) {
  const label = count === 1 ? 'Favorite' : 'Favorites';
  document.querySelectorAll(PAGE_COUNT_SELECTOR).forEach((element) => {
    element.textContent = `${count} ${label}`;
    element.hidden = false;
  });
}

function favoriteCountLabel(count) {
  return count === 1 ? 'Saved by 1 shopper' : `Saved by ${count} shoppers`;
}

function setFavoriteStat(productId, count) {
  const normalized = normalizedProductId(productId);
  if (!normalized) return;
  document.querySelectorAll(`${FAVORITE_STAT_SELECTOR}[data-product-id="${CSS.escape(productId)}"], ${FAVORITE_STAT_SELECTOR}[data-product-id="${CSS.escape(normalized)}"]`).forEach((element) => {
    element.textContent = count > 0 ? favoriteCountLabel(count) : '';
    element.hidden = count <= 0;
  });
}

async function hydrateFavoriteStats() {
  const stats = [...document.querySelectorAll(FAVORITE_STAT_SELECTOR)];
  const productIds = [...new Set(stats.map((element) => element.dataset.productId).filter(Boolean))];
  if (!productIds.length) return;

  await Promise.all(productIds.map(async (productId) => {
    try {
      const payload = await requestFavorites({ query: { product_id: productId } });
      setFavoriteStat(productId, Number(payload.favorite_count || 0));
    } catch (error) {
      setFavoriteStat(productId, 0);
    }
  }));
}

function showStatus(message, isError = false) {
  document.querySelectorAll('[data-favorites-status]').forEach((element) => {
    element.textContent = message;
    element.classList.toggle('is-error', isError);
  });
}

async function hydrateButtons() {
  const buttons = [...document.querySelectorAll(HEART_SELECTOR)];
  if (!buttons.length) return;

  try {
    const payload = await requestFavorites();
    const ids = new Set((payload.items || []).map((item) => item.product_id));
    buttons.forEach((button) => button.classList.remove('requires-login'));
    buttons.forEach((button) => setButtonState(button, ids.has(normalizedProductId(button.dataset.productId))));
    setCount(payload.count || 0);
  } catch (error) {
    if (error.payload?.error === 'login_required' || error.payload?.error === 'invalid_signature') {
      buttons.forEach((button) => {
        button.classList.add('requires-login');
        button.setAttribute('aria-pressed', 'false');
      });
      return;
    }
    showStatus('Favorites are unavailable right now. Please try again soon.', true);
  }
}

function normalizedProductId(productId) {
  if (!productId) return '';
  if (productId.startsWith('gid://shopify/Product/')) return productId;
  return `gid://shopify/Product/${productId}`;
}

async function toggleFavorite(button) {
  const productId = button.dataset.productId;
  if (!productId || button.disabled) return;

  const wasActive = button.classList.contains('is-favorited');
  button.disabled = true;
  setButtonState(button, !wasActive);

  try {
    const payload = await requestFavorites({
      method: 'POST',
      body: {
        action: wasActive ? 'remove' : 'add',
        product_id: productId,
      },
    });
    document.querySelectorAll(`${HEART_SELECTOR}[data-product-id="${CSS.escape(productId)}"]`).forEach((matchingButton) => {
      setButtonState(matchingButton, payload.favorited);
    });
    setCount(payload.count || 0);
    setFavoriteStat(productId, Number(payload.favorite_count || 0));
    if (!payload.favorited) removeFavoritesPageCard(button);
    showStatus(payload.favorited ? 'Saved to Favorites.' : 'Removed from Favorites.');
  } catch (error) {
    setButtonState(button, wasActive);
    if (error.payload?.error === 'login_required' || error.payload?.error === 'invalid_signature') {
      window.location.href = loginUrl();
      return;
    }
    showStatus('Could not update Favorites. Please try again.', true);
  } finally {
    button.disabled = false;
  }
}

function removeFavoritesPageCard(button) {
  const page = button.closest(PAGE_SELECTOR);
  const card = button.closest('.vm-favorites-card');
  if (!page || !card) return;

  card.remove();
  if (page.querySelector('.vm-favorites-card')) return;

  const grid = page.querySelector('[data-favorites-grid]');
  const empty = page.querySelector('[data-favorites-empty]');
  if (grid) grid.hidden = true;
  if (empty) empty.hidden = false;
}

async function addFavoriteToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'cart_add_failed');
    button.textContent = 'Added';
    showStatus('Added to cart.');
  } catch (error) {
    button.textContent = 'Try again';
    showStatus('Could not add this item to cart. Please try again.', true);
  } finally {
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalText;
    }, 1800);
  }
}

function productCard(item) {
  const status = item.available ? '' : '<span class="vm-favorites-card__status">Unavailable</span>';
  const addToCart = item.available && item.variant_id
    ? `<button type="button" class="vm-favorites-add-to-cart" data-favorites-add-to-cart data-variant-id="${escapeHtml(item.variant_id)}">Add to cart</button>`
    : '';
  const image = item.image_url
    ? `<img src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item.image_alt || item.title)}" loading="lazy">`
    : '<div class="vm-favorites-card__placeholder" aria-hidden="true"></div>';

  return `
    <article class="vm-favorites-card" data-product-id="${escapeHtml(item.product_id)}">
      <div class="vm-favorites-card__media">
        <a class="vm-favorites-card__image" href="${escapeHtml(item.url)}">${image}${status}</a>
        <button
          type="button"
          class="vm-favorite-button vm-favorite-button--card vm-favorite-button--favorites-card is-favorited"
          data-favorite-button
          data-product-id="${escapeHtml(item.product_id)}"
          aria-label="Remove ${escapeHtml(item.title)} from Favorites"
          aria-pressed="true"
        >
          <span class="vm-favorite-button__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M12 20.5 4.7 13.2C2.4 10.9 2.4 7.2 4.7 4.9c2-2 5.1-2.2 7.3-.5 2.2-1.7 5.3-1.5 7.3.5 2.3 2.3 2.3 6 0 8.3L12 20.5Z" />
            </svg>
          </span>
          <span class="visually-hidden" data-favorite-label>Saved to Favorites</span>
        </button>
      </div>
      <div class="vm-favorites-card__body">
        <a class="vm-favorites-card__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
        <div class="vm-favorites-card__actions">
          ${addToCart}
        </div>
      </div>
    </article>
  `;
}

async function hydrateFavoritesPage() {
  const page = document.querySelector(PAGE_SELECTOR);
  if (!page) return;

  const grid = page.querySelector('[data-favorites-grid]');
  const empty = page.querySelector('[data-favorites-empty]');
  const login = page.querySelector('[data-favorites-login]');
  const loading = page.querySelector('[data-favorites-loading]');

  try {
    const payload = await requestFavorites();
    setCount(payload.count || 0);
    if (loading) loading.hidden = true;
    if (!payload.items?.length) {
      if (empty) empty.hidden = false;
      return;
    }
    if (grid) {
      grid.innerHTML = payload.items.map(productCard).join('');
      grid.hidden = false;
    }
  } catch (error) {
    if (loading) loading.hidden = true;
    if (error.payload?.error === 'login_required' || error.payload?.error === 'invalid_signature') {
      if (login) login.hidden = false;
      return;
    }
    showStatus('Favorites are unavailable right now. Please try again soon.', true);
  }
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (char) => {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return entities[char];
  });
}

document.addEventListener('click', (event) => {
  const addToCartButton = event.target.closest(ADD_TO_CART_SELECTOR);
  if (addToCartButton) {
    event.preventDefault();
    event.stopPropagation();
    addFavoriteToCart(addToCartButton);
    return;
  }

  const button = event.target.closest(HEART_SELECTOR);
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  toggleFavorite(button);
});

hydrateButtons();
hydrateFavoriteStats();
hydrateFavoritesPage();
